import React, { useMemo, useEffect, useCallback } from "react";

import { createAccount, CreateAccountResult } from "src/customer/create_account";
import { ReactComponent as RemoveIcon } from "images/icons/remove.svg?react";

const CreateAccountForm = ({
  afterCreateAccountCallback,
  signInSelectCallback,
  onClose,
  initialEmail,
  initialPassword,
  initialPasswordConfirmation,
  initialFirstName,
  initialLastName,
  initialPhoneNumber,
}: {
  afterCreateAccountCallback: () => void;
  signInSelectCallback: () => void;
  onClose?: () => void;
  initialEmail?: string;
  initialPassword?: string;
  initialPasswordConfirmation?: string;
  initialFirstName?: string;
  initialLastName?: string;
  initialPhoneNumber?: string;
}) => {
  const [email, setEmail] = React.useState(initialEmail);
  const [password, setPassword] = React.useState(initialPassword);
  const [passwordConfirmation, setPasswordConfirmation] = React.useState(initialPasswordConfirmation);
  const [firstName, setFirstName] = React.useState(initialFirstName);
  const [lastName, setLastName] = React.useState(initialLastName);
  const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber);

  const [errors, setErrors] = React.useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const headerRef = React.useRef<HTMLElement>(null);
  const createAccountFormRef = React.createRef<HTMLFormElement>();

  const setScrollMargins = useCallback(() => {
    const createAccountForm = createAccountFormRef.current;
    const header = headerRef.current;

    if (createAccountForm && header) {
      const labelsAndInputs: NodeListOf<HTMLLabelElement | HTMLInputElement> = createAccountForm.querySelectorAll("label, input");
      labelsAndInputs.forEach((element) => {
        element.style.scrollMarginTop = header.offsetHeight + "px";
      })
    }
  }, [createAccountFormRef]);

  const scrollToError = useCallback(() => {
    const errorKeys = Object.keys(errors);

    if (errorKeys.length > 0) {
      const selector = errorKeys.map((key) => errors[key].length > 0 ? `label[for='${key}']` : null).join(", ");

      createAccountFormRef.current?.querySelector(selector)?.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [errors, createAccountFormRef]);

  useEffect(() => {
    setScrollMargins()
    scrollToError();
  }, [errors, setScrollMargins, scrollToError])

  const submitSignUp = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);

    if (!createAccountFormRef.current) { return; }

    if (createAccountFormRef.current) {
      const formData = new FormData(createAccountFormRef.current);
      formData.set("authenticity_token", (document.querySelector("meta[name='csrf-token']") as HTMLMetaElement)?.content || "");

      createAccount(formData)
        .then(handleCreateAccountResponse)
        .catch(handleCreateAccountError);
    }
  }

  const handleCreateAccountResponse = (response: CreateAccountResult) => {
    if (response.errors) {
      setErrors(response.errors);
    } else {
      afterCreateAccountCallback();
    }

    setIsSubmitting(false);
  }

  const handleCreateAccountError = () => {
    setErrors({ base: ["Error trying to sign up. Please try again later"] });
    setIsSubmitting(false);
  }

  const onSignInSelect = () => {
    if (isSubmitting) { return false; }

    signInSelectCallback();
  }

  const fieldErrors = (errorKey: string) => {
    const fieldErrorMessages = errors[errorKey] || [];

    if (fieldErrorMessages.length > 0) {
      return (
        <div className="error" data-testid="fieldError">
          {fieldErrorMessages.join(". ")}
        </div>
      );
    } else {
      return null;
    }
  }

  const hasAnyErrors = useMemo(() => Object.values(errors).some((errors) => errors.length > 0), [errors])

  return (
    <>
      <section className="divider has-content has-high-contrast is-stuck-top is-layer-middle" ref={headerRef}>
        <h5 className="space-mb-none">Sign up</h5>
        {onClose &&
          <button className="button is-icon-only has-large-icon is-ghost is-aligned-right" name="button" aria-label="Close" onClick={onClose}>
            <RemoveIcon className="icon streamline-icon" />
          </button>
        }
      </section>

      <section className="content space-pt-none">
        <form onSubmit={submitSignUp} ref={createAccountFormRef}>
          <fieldset className={hasAnyErrors ? "has-error" : undefined}>
            <div className="text-input has-max-width">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                autoComplete="email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {fieldErrors("email")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                autoComplete="new-password"
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div className="helper">
                Must be at least 8 characters
              </div>
              {fieldErrors("password")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="password_confirmation">Password confirmation</label>
              <input
                id="password_confirmation"
                type="password"
                name="password_confirmation"
                autoComplete="new-password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                required
              />
              {fieldErrors("password_confirmation")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="first_name">First name</label>
              <input
                id="first_name"
                type="text"
                name="first_name"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              {fieldErrors("first_name")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="last_name">Last name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={lastName}
                autoComplete="family-name"
                onChange={(e) => setLastName(e.target.value)}
                required
              />
              {fieldErrors("last_name")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="phone_number">Phone number</label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={phoneNumber}
                autoComplete="tel"
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
              />
              {fieldErrors("phone_number")}
            </div>

            {fieldErrors("base")}
          </fieldset>

          <button type="submit" className="button is-primary" disabled={isSubmitting}>Sign up</button>
        </form>

        <p className="space-mt-xs">
          Already have an account? <a className="has-underline" onClick={onSignInSelect}>Sign in</a>
        </p>
      </section>
    </>
  );
}

export default CreateAccountForm;